import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Position,
  Panel,
} from "reactflow";
import dagre from 'dagre';
import "reactflow/dist/style.css";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
} from "@tonic/shared/components/ui";
import { useTheme } from "libs/shared/hooks/use-theme.js"
import { AILearningNode } from "./custom-nodes/ai-learning-node";
import { CustomerNode } from "./custom-nodes/customer-node";
import { ContextNode } from "./custom-nodes/context-node";
import { AlertCircle, Loader2, Calendar, User, Brain } from "lucide-react";

const nodeTypes = {
  aiLearning: AILearningNode,
  customer: CustomerNode,
  context: ContextNode,
};

const nodeWidth = 180;
const nodeHeight = 80;

const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 70, ranksep: 100 });
  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes to the graph with their dimensions
  nodes.forEach((node) => {
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to the graph
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(g);

  // Get the positioned nodes
  const positionedNodes = nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
      // Add default node styling if not present
      style: {
        width: nodeWidth,
        ...node.style,
      },
    };
  });

  return { nodes: positionedNodes, edges };
};

const DomainKnowledgeGraph = ({ data, onNodeClick, className, ...props }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    try {
      if (data?.nodes && data?.edges) {
        if (data.nodes.length === 0) {
          setError('No nodes available in the knowledge graph');
          return;
        }

        // Apply layout to the graph
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
          data.nodes.map(node => ({
            ...node,
            // Add any missing required properties
            type: node.type || 'default',
            data: { label: node.label, ...node.data },
          })),
          data.edges.map(edge => ({
            ...edge,
            // Add any missing required properties
            id: edge.id || `${edge.source}-${edge.target}`,
            type: edge.type || 'default',
            animated: true, // Add animation to edges
            style: { stroke: '#666', strokeWidth: 1.5 },
          }))
        );

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        setError(null);
      }
    } catch (err) {
      console.error('Error laying out graph:', err);
      setError('Failed to layout the knowledge graph');
    }
  }, [data, setNodes, setEdges]);

  const onLayout = useCallback(() => {
    try {
      if (nodes.length === 0) return;

      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        nodes,
        edges
      );

      setNodes([...layoutedNodes]);
      setEdges([...layoutedEdges]);
      setError(null);
    } catch (err) {
      console.error('Error re-laying out graph:', err);
      setError('Failed to re-layout the knowledge graph');
    }
  }, [nodes, edges, setNodes, setEdges]);

  if (error) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data?.nodes) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={`h-[600px] ${className}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onNodeClick={(_, node) => onNodeClick?.(node)}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        defaultViewport={{ zoom: 1 }}
        minZoom={0.1}
        maxZoom={2}
        {...props}
      >
        <Background variant="dots" gap={12} size={1} />
        <Controls showInteractive={false} />
        <Panel position="top-right">
          <Button
            variant="secondary"
            size="sm"
            onClick={onLayout}
            className="mb-2"
          >
            Reset Layout
          </Button>
        </Panel>
      </ReactFlow>
    </div>
  );
};

/**
 * @typedef {Object} Props
 * @property {*} selectedInsight? | null
 * @property {*} onClearSelection?
 */

/**
 * Helper function for date formatting
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted date string
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Knowledge Graph Visualization component
 * @param {Object} props - Component props
 * @param {string} props.className - CSS class name for styling
 * @param {Object} props.data - Graph data containing nodes and edges
 * @param {Function} props.onNodeClick - Callback function when a node is clicked
 * @returns {React.ReactElement} Knowledge graph visualization component
 */
export function KnowledgeGraphVisualization({
  className,
  data,
  onNodeClick,
  ...props
}) {
  const { theme } = useTheme();

  return (
    <div
      className={`border border-${theme === "dark" ? "neutral-800" : "gray-200"
        } rounded-lg ${className}`}
    >
      <div className="p-4">
        <h2
          className={`text-lg font-semibold mb-4 text-${theme === "dark" ? "white" : "gray-900"
            }`}
        >
          Knowledge Graph
        </h2>
        <DomainKnowledgeGraph
          data={data}
          onNodeClick={onNodeClick}
          className={`bg-${theme === "dark" ? "neutral-800" : "white"}`}
          {...props}
        />
      </div>
    </div>
  );
}

KnowledgeGraphVisualization.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    nodes: PropTypes.arrayOf(PropTypes.object),
    edges: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onNodeClick: PropTypes.func,
};

KnowledgeGraphVisualization.defaultProps = {
  className: "",
  onNodeClick: () => { },
};
